import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { useNotify } from '../../context/NotificationContext';
import { ADDR, areContractsConfigured } from '../../config/contracts';
import { isSameAddress, shortAddress } from '../../utils/format';
import { ButtonSpinner } from '../common/Loader';
import { parseError } from '../../utils/errors';
import { Alert } from '../common/StatBox';

// Checks Storage.authorizedLogic AND Storage.authorizedAdmin. Both must
// point to the deployed Logic / Admin contracts. Offers the OWNER one-click
// linking for whichever slot is unset.
export const ContractStatus = () => {
  const { storageR, storageW } = useContracts();
  const { isOwner, ownerAddr, adminAddr } = useAdminCheck();
  const notify = useNotify();

  const [authorizedLogic, setAuthorizedLogic] = useState(null);
  const [authorizedAdmin, setAuthorizedAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);
  const [configured, setConfigured] = useState(areContractsConfigured());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const isConf = areContractsConfigured();
      setConfigured(isConf);
      if (!storageR || !isConf) {
        setLoading(false);
        return;
      }
      try {
        const [al, aa] = await Promise.all([
          storageR.authorizedLogic(),
          storageR.authorizedAdmin()
        ]);
        if (cancelled) return;
        setAuthorizedLogic(al);
        setAuthorizedAdmin(aa);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [storageR, reload]);

  const logicLinked = authorizedLogic && isSameAddress(authorizedLogic, ADDR.logic);
  const adminLinked = authorizedAdmin && isSameAddress(authorizedAdmin, ADDR.admin);
  const bothLinked  = logicLinked && adminLinked;

  const linkOne = async (which) => {
    if (!storageW) return;
    if (!isOwner) {
      notify.error('Only contract owner can connect contracts');
      return;
    }
    setBusy(which);
    try {
      const tx = which === 'logic'
        ? await storageW.setAuthorizedLogic(ADDR.logic)
        : await storageW.setAuthorizedAdmin(ADDR.admin);
      notify.info(`Linking ${which}...`);
      await tx.wait();
      notify.success(`${which === 'logic' ? 'Logic' : 'Admin'} contract linked!`);
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!configured) {
    return (
      <div className="contract-status-card error">
        <div className="status-icon-lg">⛔</div>
        <div style={{ flex: 1 }}>
          <h3 style={{ marginBottom: 6 }}>Contract Addresses Not Configured</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Set the Storage, Logic, Admin, Views, and USDC addresses in <code style={{ background: 'var(--bg-panel)', padding: '2px 6px', borderRadius: 4 }}>src/config/contracts.js</code> before using the app.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="contract-status-card unlinked">
        <div className="status-icon-lg">⏳</div>
        <div>Checking contract links...</div>
      </div>
    );
  }

  if (bothLinked) {
    return (
      <div className="contract-status-card linked">
        <div className="status-icon-lg">✓</div>
        <div style={{ flex: 1 }}>
          <h3 style={{ marginBottom: 6 }}>All Contracts Connected</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Storage → Logic and Storage → Admin links are both verified. All actions can proceed.
          </p>
          <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-tertiary)' }}>
            Owner: {shortAddress(ownerAddr, 6)} · Admin: {shortAddress(adminAddr, 6)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-status-card unlinked">
      <div className="status-icon-lg">🔗</div>
      <div style={{ flex: 1 }}>
        <h3 style={{ marginBottom: 6 }}>Contracts Not Fully Linked</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 12 }}>
          Storage must authorize both the Logic and Admin contracts. Only the owner can set these.
        </p>

        <div className="kv-list mb-16" style={{ fontSize: 13 }}>
          <div className="kv-row">
            <span className="kv-key">Logic slot</span>
            <span className="kv-val" style={{ color: logicLinked ? 'var(--neon-green)' : 'var(--neon-red)' }}>
              {logicLinked ? '✓ Linked' : (authorizedLogic || '— unset —')}
            </span>
          </div>
          <div className="kv-row">
            <span className="kv-key">Admin slot</span>
            <span className="kv-val" style={{ color: adminLinked ? 'var(--neon-green)' : 'var(--neon-red)' }}>
              {adminLinked ? '✓ Linked' : (authorizedAdmin || '— unset —')}
            </span>
          </div>
          {!logicLinked && (
            <div className="kv-row">
              <span className="kv-key">Expected Logic</span>
              <span className="kv-val">{ADDR.logic}</span>
            </div>
          )}
          {!adminLinked && (
            <div className="kv-row">
              <span className="kv-key">Expected Admin</span>
              <span className="kv-val">{ADDR.admin}</span>
            </div>
          )}
        </div>

        {isOwner ? (
          <div className="flex flex-wrap">
            {!logicLinked && (
              <button className="btn btn-gold" onClick={() => linkOne('logic')} disabled={busy === 'logic'}>
                {busy === 'logic' && <ButtonSpinner />}Link Logic
              </button>
            )}
            {!adminLinked && (
              <button className="btn btn-gold" onClick={() => linkOne('admin')} disabled={busy === 'admin'}>
                {busy === 'admin' && <ButtonSpinner />}Link Admin
              </button>
            )}
          </div>
        ) : (
          <Alert type="warning">Please connect with the owner wallet to link contracts.</Alert>
        )}
      </div>
    </div>
  );
};
